// src/redux/slices/siteContentSlice.ts
import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { SiteContent } from '../../types/siteContent';
import type { RootState } from '../store';
import { replaceMenuWidget } from '../../utils/widgetUtils';

interface SiteContentState {
  data: SiteContent | null;
  websiteConfig: any | null; // Store full website config for PUT requests
  fetchLoading: boolean;
  updateLoading: boolean;
  error: string | null;
}

const initialState: SiteContentState = {
  data: null,
  websiteConfig: null,
  fetchLoading: false,
  updateLoading: false,
  error: null,
};

export const siteContentSlice = createSlice({
  name: 'siteContent',
  initialState,
  reducers: {
    fetchSiteContentRequest: state => {
      state.fetchLoading = true;
      state.error = null;
    },
    fetchSiteContentSuccess: (state, action: PayloadAction<{ siteContent: SiteContent; websiteConfig?: any }>) => {
      state.fetchLoading = false;
      state.data = action.payload.siteContent ? structuredClone(action.payload.siteContent) : null;
      state.websiteConfig = action.payload.websiteConfig || null;
      state.error = null;
    },
    fetchSiteContentFailure: (state, action: PayloadAction<string>) => {
      state.fetchLoading = false;
      state.error = action.payload;
      state.data = null;
    },

    // Update widget data - Proper Immer pattern (in-place mutation)
    updateWidgetData: (state, action: PayloadAction<{ index: number; data: any }>) => {
      const { index, data } = action.payload;

      if (state.data?.widgets && state.data.widgets[index]) {
        // Mutate in place (Immer tracks this safely)
        state.data.widgets[index].data = data;

        if (state.data.homeWidgets) {
          const homeWidgetIndex = state.data.homeWidgets.findIndex(
            (widget: any) => widget.type === state.data!.widgets![index].type
          );

          if (homeWidgetIndex >= 0) {
            state.data.homeWidgets[homeWidgetIndex].data = data;
          }
        }
      }
    },

    // Add new widget section (for home page widgets only)
    addWidgetSection: (
  state,
  action: PayloadAction<{ widget: any; afterIndex?: number }>
) => {
  if (!state.data?.widgets) {
    return;
  }

  const { widget, afterIndex } = action.payload;
  
  if (afterIndex === undefined) {
    state.data.widgets.push(widget);
    if (state.data.homeWidgets) {
      state.data.homeWidgets.push(widget);
    }
  } else if (afterIndex >= 0) {
    state.data.widgets.splice(afterIndex + 1, 0, widget);

    if (state.data.homeWidgets) {
      const targetWidget = state.data.widgets[afterIndex];
      const targetType = targetWidget?.type;
      const homeWidgetIndex = state.data.homeWidgets.findIndex(
        (w: any) => w.type === targetType
      );

      if (homeWidgetIndex >= 0) {
        state.data.homeWidgets.splice(homeWidgetIndex + 1, 0, widget);
      } else {
        state.data.homeWidgets.push(widget);
      }
    }
  }
},

    // Delete widget section (for home page widgets only)
    deleteWidgetSection: (state, action: PayloadAction<number>) => {
      if (!state.data?.widgets || !state.data.widgets[action.payload]) {
        return;
      }

      const widgetIndex = action.payload;
      const widgetToDelete = state.data.widgets[widgetIndex];
      const widgetType = widgetToDelete?.type;
      
      state.data.widgets.splice(widgetIndex, 1);
      
      if (state.data.homeWidgets && widgetType) {
        const homeWidgetIndex = state.data.homeWidgets.findIndex(
          (widget: any) => widget.type === widgetType
        );
        
        if (homeWidgetIndex >= 0) {
          state.data.homeWidgets.splice(homeWidgetIndex, 1);
        }
      }
    },

    // Update site content actions
    updateSiteContentRequest: state => {
      state.updateLoading = true;
      state.error = null;
    },
    updateSiteContentSuccess: (state, action: PayloadAction<SiteContent>) => {
      state.updateLoading = false;
      state.data = action.payload ? structuredClone(action.payload) : null;
      state.error = null;
    },
    updateSiteContentFailure: (state, action: PayloadAction<string>) => {
      state.updateLoading = false;
      state.error = action.payload;
    },

    // Clear site content
    clearSiteContent: state => {
      state.data = null;
      state.websiteConfig = null;
      state.error = null;
      state.fetchLoading = false;
      state.updateLoading = false;
    },

    // Manual reset for stuck loading states
    resetLoadingStates: state => {
      state.fetchLoading = false;
      state.updateLoading = false;
    },

    // Replace menu widget type while preserving data
    replaceMenuWidgetType: (state, action: PayloadAction<string>) => {
      if (state.data) {
        // Update widgets array using utility function
        if (state.data.widgets) {
          state.data.widgets = replaceMenuWidget(
            state.data.widgets,
            action.payload
          );
        }
      }
    },

    // Change widget type while preserving data (for home page widgets)
    changeWidgetType: (
      state,
      action: PayloadAction<{ widgetIndex: number; newVariant: string }>
    ) => {
      if (!state.data?.widgets) {
        return;
      }

      const { widgetIndex, newVariant } = action.payload;
      const widget = state.data.widgets[widgetIndex];

      if (!widget) {
        return;
      }

      // Use newVariant directly as the new type
      const oldType = widget.type;

      // Update widget type in widgets array
      state.data.widgets[widgetIndex].type = newVariant;

      // Update in homeWidgets array if it exists
      if (state.data.homeWidgets) {
        const homeWidgetIndex = state.data.homeWidgets.findIndex(
          (w: any) => w.type === oldType
        );

        if (homeWidgetIndex >= 0) {
          state.data.homeWidgets[homeWidgetIndex].type = newVariant;
        }
      }
    },

    // Change widget type for page widgets (dynamic pages)
    changePageWidgetType: (
      state,
      action: PayloadAction<{ slug: string; widgetIndex: number; newVariant: string }>
    ) => {
      if (!state.data?.pages?.[action.payload.slug]) {
        return;
      }

      const { slug, widgetIndex, newVariant } = action.payload;
      const widget = state.data.pages[slug].widgets[widgetIndex];

      if (!widget) {
        return;
      }

      // Use newVariant directly as the new type
      state.data.pages[slug].widgets[widgetIndex].type = newVariant;
    },

    // Dynamic CMS Page Management
    createPage: (state, action: PayloadAction<{ slug: string; widgets?: any[]; title?: string }>) => {
      if (!state.data) {
        return;
      }

      if (!state.data.pages) {
        state.data.pages = {};
      }

      const { slug, widgets = [], title } = action.payload;

      if (state.data.pages[slug]) {
        return;
      }

      state.data.pages[slug] = {
        widgets,
        title,
      };
    },

    updatePageWidgets: (
      state,
      action: PayloadAction<{ slug: string; widgets: any[] }>
    ) => {
      if (!state.data?.pages) {
        return;
      }

      const { slug, widgets } = action.payload;

      if (!state.data.pages[slug]) {
        return;
      }

      state.data.pages[slug].widgets = widgets;
    },

    // Add widget to specific page only (page-level operation)
    addPageWidget: (
      state,
      action: PayloadAction<{ slug: string; widget: any; afterIndex?: number }>
    ) => {
      if (!state.data?.pages?.[action.payload.slug]) {
        return;
      }

      const { slug, widget, afterIndex } = action.payload;
      const widgets = state.data.pages[slug].widgets;

      if (afterIndex === undefined) {
        widgets.push(widget);
      } else {
        widgets.splice(afterIndex + 1, 0, widget);
      }
    },

    // Delete widget from specific page only (page-level operation)
    deletePageWidget: (
      state,
      action: PayloadAction<{ slug: string; index: number }>
    ) => {
      if (!state.data?.pages?.[action.payload.slug]) {
        return;
      }

      const { slug, index } = action.payload;
      state.data.pages[slug].widgets.splice(index, 1);
    },

    // Update widget data in specific page
    updatePageWidgetData: (
      state,
      action: PayloadAction<{ slug: string; index: number; data: any }>
    ) => {
      if (!state.data?.pages?.[action.payload.slug]) {
        return;
      }

      const { slug, index, data } = action.payload;
      if (state.data.pages[slug].widgets[index]) {
        state.data.pages[slug].widgets[index].data = data;
      }
    },

    deletePage: (state, action: PayloadAction<string>) => {
      if (!state.data?.pages) {
        return;
      }

      const slug = action.payload;

      if (!state.data.pages[slug]) {
        return;
      }

      delete state.data.pages[slug];
    },

  },
});

export const {
  fetchSiteContentRequest,
  fetchSiteContentSuccess,
  fetchSiteContentFailure,
  updateWidgetData,
  addWidgetSection,
  deleteWidgetSection,
  updateSiteContentRequest,
  updateSiteContentSuccess,
  updateSiteContentFailure,
  clearSiteContent,
  resetLoadingStates,
  replaceMenuWidgetType,
  changeWidgetType,
  changePageWidgetType,
  createPage,
  updatePageWidgets,
  addPageWidget,
  deletePageWidget,
  updatePageWidgetData,
  deletePage,
} = siteContentSlice.actions;

// Safe Selector (Deep clone result)
export const selectPlainSiteContent = createSelector(
  (state: RootState) => state.siteContent.data,
  (data) => data ? JSON.parse(JSON.stringify(data)) : null
);

export default siteContentSlice.reducer;
